// What the quote screen wears before its data arrives, in two stages:
// the chrome and heading render instantly with a pulsing bar where the
// name goes and three anonymous card outlines in the cards region; once
// the packages flag resolves, those become one fully-structured card
// skeleton per package — its client-side identity (kicker, name,
// tagline, pills) real, its prices and features pulsing. The region is
// card-shaped from the first paint and never collapses; the sticky bar
// holds its loading state with Continue disabled throughout.

import { ChevronRightIcon } from "@heroicons/react/24/outline";

import Button from "components/Button";
import Skeleton from "components/Skeleton";

import {
  GuaranteePill,
  IncludedInAllIllustration,
  PLAN_COPY,
  PLAN_ORDER,
  soloCopy,
} from "./PricingCards";

/** Holds the real feature list's exact layout — `gap-2.5` rows of a
 * `mt-0.5` 16px icon beside `text-base leading-snug` text — with each
 * bar centered in the 22px line box its text will occupy. */
function FeatureListSkeleton({ widths }: { widths: string[] }) {
  return (
    <ul className="flex flex-col gap-2.5">
      {widths.map((width) => (
        <li key={width} className="flex items-start gap-2.5">
          <Skeleton className="mt-0.5 h-4 w-4 shrink-0 rounded-sm" />
          <span className={`flex h-[22px] items-center ${width}`}>
            <Skeleton className="h-[18px] w-full" />
          </span>
        </li>
      ))}
    </ul>
  );
}

/** Before the packages flag resolves, nothing knows WHICH cards render —
 * but three cards always do (a hidden Base yields its slot to the
 * included-in-all panel, same footprint). Three anonymous outlines hold
 * the grid so the identity-bearing skeletons land without a reflow. */
function AnonymousCardSkeleton() {
  return (
    <div className="flex flex-col gap-4 rounded-2xl border-2 border-oat-200 bg-white p-4 shadow-sm 2xs:p-6">
      <Skeleton className="h-3.5 w-24" />
      <div>
        <span className="flex h-[34px] items-center lg:h-[42px]">
          <Skeleton className="h-7 w-40 lg:h-8" />
        </span>
        <span className="mt-1 flex min-h-[48px] items-start pt-0.5">
          <Skeleton className="h-[18px] w-4/5" />
        </span>
      </div>
      <div className="rounded-xl bg-oat-50 px-3 py-4 2xs:px-4">
        <Skeleton className="h-3.5 w-20" />
        <div className="mt-2 flex items-baseline gap-1.5">
          <Skeleton className="h-8 w-28 lg:h-10" />
        </div>
        <div className="mt-3 border-t border-oat-200 pt-3">
          <Skeleton className="h-4 w-44" />
        </div>
        <Skeleton className="mt-4 h-12 w-full rounded-md" />
      </div>
      <FeatureListSkeleton widths={["w-4/5", "w-3/5", "w-full", "w-2/3"]} />
    </div>
  );
}

function AnonymousCardsGrid() {
  return (
    <div className="mt-7 flex flex-col gap-4 lg:grid lg:grid-cols-3 lg:items-start lg:gap-5">
      {["left", "middle", "right"].map((slot) => (
        <AnonymousCardSkeleton key={slot} />
      ))}
    </div>
  );
}

function PlanCardSkeleton({ slug }: { slug: string }) {
  const copy = PLAN_COPY[slug];
  if (!copy) return null;
  return (
    <div
      className={`flex flex-col gap-4 rounded-2xl border-2 border-oat-200 bg-white p-4 text-soil shadow-sm 2xs:p-6 ${copy.stackedOrder}`}
    >
      <span
        className={`text-[13px] font-bold uppercase tracking-[0.14em] ${copy.kickerClass}`}
      >
        {copy.kicker}
      </span>

      <div>
        <h2 className="font-serif text-[25px] leading-tight text-soil lg:text-[31px]">
          {copy.name} <span className="text-oat-700">package</span>
        </h2>
        <p className="mt-1 min-h-[48px] text-base leading-normal text-oat-700">
          {copy.tagline}
        </p>
      </div>

      <div className="rounded-xl bg-oat-50 px-3 py-4 2xs:px-4">
        <span className="text-[13px] font-bold uppercase tracking-[0.14em] text-oat-700">
          Starting at
        </span>
        <div className="mt-2 flex items-baseline gap-1.5">
          <Skeleton className="h-8 w-28 lg:h-10" />
          <span className="text-sm text-oat-700">per month</span>
        </div>
        <div className="mt-3 border-t border-oat-200 pt-3">
          <Skeleton className="h-4 w-44" />
        </div>
        <Skeleton className="mt-4 h-12 w-full rounded-md" />
      </div>

      <div className="flex flex-col items-start gap-2">
        {copy.pills.map((pill) => (
          <GuaranteePill key={pill.label} pill={pill} selected={false} />
        ))}
      </div>

      <FeatureListSkeleton
        widths={["w-4/5", "w-3/5", "w-full", "w-2/3", "w-3/4"]}
      />
    </div>
  );
}

/** The "what all plans include" panel's loading stance: real heading,
 * pulsing feature lines — it holds Base's slot when the flag hides the
 * Base card, so the reveal never reshapes the grid. */
function IncludedInAllPanelSkeleton() {
  return (
    <div
      className={`flex flex-col gap-4 overflow-hidden rounded-2xl border border-oat-200 bg-oat-50 p-4 text-soil 2xs:p-6 ${
        PLAN_COPY.simple?.stackedOrder ?? ""
      }`}
    >
      <span className="text-[13px] font-bold uppercase tracking-[0.14em] text-evergreen">
        The essentials
      </span>
      <div>
        <h2 className="font-serif text-[25px] leading-tight text-soil lg:text-[31px]">
          What all plans <span className="text-oat-700">include</span>
        </h2>
        <p className="mt-1 min-h-[48px] text-base leading-normal text-oat-700">
          Every package builds on the same essential care.
        </p>
      </div>
      <FeatureListSkeleton
        widths={["w-3/4", "w-2/3", "w-4/5", "w-3/5", "w-full"]}
      />
      <IncludedInAllIllustration />
    </div>
  );
}

/**
 * The single-plan screen's loading stance: two cards, not three. The
 * headings are real from the first paint — they say nothing the flags
 * could still change — while the list and the prices pulse.
 */
function SinglePlanCardsSkeleton({ slug }: { slug: string }) {
  const copy = soloCopy(slug);
  // Mirrors GuaranteeBadges: only guarantees the plan HAS get a badge,
  // so the skeleton reserves the right number of them.
  const badges = (PLAN_COPY[slug]?.pills ?? []).filter((pill) => !pill.missing);
  return (
    <div className="mx-auto mt-7 flex max-w-[1080px] flex-col gap-4 lg:grid lg:grid-cols-2 lg:items-stretch lg:gap-6">
      <div className="flex flex-col gap-4 rounded-2xl border border-oat-200 bg-oat-50 p-4 text-soil 2xs:p-6">
        <div>
          <h2 className="font-serif text-[25px] leading-tight text-soil lg:text-[31px]">
            Your future is covered
          </h2>
          <p className="mt-1 text-base leading-normal text-oat-700">
            Your plan includes everything you need for ultimate peace of mind
          </p>
        </div>
        {badges.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {badges.map((pill) => (
              <Skeleton
                key={pill.label}
                className="h-[30px] w-44 rounded-full"
              />
            ))}
          </div>
        ) : null}
        <FeatureListSkeleton
          widths={[
            "w-3/4",
            "w-full",
            "w-2/3",
            "w-4/5",
            "w-3/5",
            "w-3/4",
            "w-1/2",
            "w-2/3",
            "w-1/2",
            "w-4/5",
            "w-3/5",
            "w-full",
          ]}
        />
      </div>
      <div className="flex flex-col gap-4 rounded-2xl border-2 border-evergreen bg-evergreen p-4 text-ivory shadow-xl 2xs:p-6">
        <div>
          <h2 className="font-serif text-[25px] leading-tight text-ivory lg:text-[31px]">
            {copy.headline}
          </h2>
          <p className="mt-1 text-base leading-normal text-ivory/90">
            {copy.support}
          </p>
        </div>
        <div className="rounded-xl bg-ivory-light/10 px-3 py-4 2xs:px-4">
          <span className="text-[13px] font-bold uppercase tracking-[0.14em] text-ivory/90">
            Starting at
          </span>
          <div className="mt-2 flex items-baseline gap-1.5">
            <Skeleton className="h-8 w-28 lg:h-10" />
            <span className="text-sm text-ivory/90">per month</span>
          </div>
          <div className="mt-3 border-t border-ivory-light/20 pt-3">
            <Skeleton className="h-4 w-44" />
          </div>
        </div>
        <div className="flex flex-col gap-2">
          <span className="text-[13px] font-bold uppercase tracking-[0.14em] text-ivory/80">
            How you&rsquo;d like to pay
          </span>
          <Skeleton className="h-[86px] w-full rounded-[10px]" />
          <Skeleton className="h-[86px] w-full rounded-[10px]" />
        </div>
      </div>
    </div>
  );
}

/**
 * `planSlugs` null = the packages flag hasn't resolved: the cards region
 * spins. Resolved = one structured skeleton per package it names, with
 * the included-in-all panel holding Base's slot when Base is hidden —
 * or, under the single-plan treatment, the two-card stance so the
 * reveal never reshapes the grid.
 */
export default function QuoteScreenSkeleton({
  planSlugs,
  singlePlan,
}: {
  planSlugs: string[] | null;
  singlePlan: boolean;
}) {
  const ordered = planSlugs
    ? [...planSlugs].sort(
        (a, b) => PLAN_ORDER.indexOf(a) - PLAN_ORDER.indexOf(b),
      )
    : null;

  return (
    <div className="flex flex-1 flex-col">
      <div className="mx-auto w-full max-w-[1180px] flex-1 px-4 pb-8 pt-6 2xs:px-5 sm:px-10 sm:pt-11">
        <span className="text-[13px] font-bold uppercase tracking-[0.14em] text-evergreen">
          Your quote
        </span>
        <h1 className="mt-2 max-w-[60ch] font-serif text-[39px] font-normal leading-[1.12] text-soil md:text-[49px]">
          Here&rsquo;s your quote,{" "}
          <Skeleton className="h-[0.75em] w-40 align-baseline md:w-52" />
        </h1>

        {ordered === null ? (
          <AnonymousCardsGrid />
        ) : singlePlan && ordered[0] ? (
          <SinglePlanCardsSkeleton slug={ordered[0]} />
        ) : (
          <div className="mt-7 flex flex-col gap-4 lg:grid lg:grid-cols-3 lg:items-start lg:gap-5">
            {!ordered.includes("simple") && <IncludedInAllPanelSkeleton />}
            {ordered.map((slug) => (
              <PlanCardSkeleton key={slug} slug={slug} />
            ))}
          </div>
        )}
      </div>

      <div className="sticky bottom-0 z-30 border-t border-oat-200 bg-ivory-light shadow-[0_-8px_24px_-16px_rgba(80,58,58,0.4)]">
        <div className="mx-auto flex w-full max-w-[1180px] flex-col gap-3 px-4 py-4 2xs:px-5 sm:flex-row sm:items-center sm:gap-6 sm:px-10">
          <div className="flex-1">
            <p className="text-base font-bold text-soil">
              One moment — we&rsquo;re finalizing your prices
            </p>
            <p className="text-[13px] text-oat-700">
              This only takes a few seconds
            </p>
          </div>
          <Button variant="continue" className="relative" disabled>
            <span className="mx-3">Continue</span>
            <ChevronRightIcon
              className="absolute right-[13px] top-1/2 h-4 w-4 -translate-y-1/2"
              strokeWidth={2.5}
              aria-hidden="true"
            />
          </Button>
        </div>
      </div>
    </div>
  );
}
